// A self-contained "thinking" cue for the voice assistant.
//
// When the user asks the voice agent something, the answer is fetched from our
// backend (planner -> executor -> synthesizer), which can take several seconds.
// During that round trip the ElevenLabs agent is silent, which reads as "it
// broke." This plays a soft, gently-pulsing pad for the duration of that fetch
// so the user knows we're working on it.
//
// Synthesized with the Web Audio API on purpose: no audio file to ship or load,
// and the volume/character is tunable here in one place. Two detuned sine
// oscillators through a low-pass filter give a warm, muffled tone (not a phone
// hold-tone), and a slow tremolo makes it "breathe" ~once a second so it reads
// as active processing rather than a flat drone.

const TARGET_GAIN = 0.06; // master volume — subtle, sits under the agent's voice
const FADE_IN_SECS = 0.2;
const FADE_OUT_SECS = 0.35;

type Nodes = {
  master: GainNode;
  oscillators: OscillatorNode[];
  tremolo: OscillatorNode;
};

let ctx: AudioContext | null = null;
let nodes: Nodes | null = null;

function getContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null; // very old browser — degrade to no sound, never throw
  ctx = new Ctor();
  return ctx;
}

type ChimeNote = { freq: number; at: number; dur: number };

type ChimeOpts = { peak?: number; attack?: number; cutoff?: number };

// Shared chime builder. Each note is a pure sine plus a quiet octave-up shimmer.
// `attack` + `cutoff` shape the character: longer attack / low cutoff = a warm
// swell; shorter attack / higher cutoff = brighter and bouncier. Sine + the
// shimmer keep it pleasant rather than a cheap beep at either setting.
function playChime(
  notes: ChimeNote[],
  { peak = 0.1, attack = 0.05, cutoff = 1400 }: ChimeOpts = {},
): void {
  const audio = getContext();
  if (!audio) return;
  void audio.resume();

  const now = audio.currentTime;

  const lowpass = audio.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = cutoff;
  lowpass.connect(audio.destination);

  notes.forEach(({ freq, at, dur }) => {
    const startAt = now + at;
    [
      { f: freq, g: peak },
      { f: freq * 2, g: peak * 0.18 }, // faint octave for richness
    ].forEach(({ f, g }) => {
      const osc = audio.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      const gain = audio.createGain();
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.linearRampToValueAtTime(g, startAt + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
      osc.connect(gain);
      gain.connect(lowpass);
      osc.start(startAt);
      osc.stop(startAt + dur + 0.03);
    });
  });
}

/**
 * Upbeat rising major arpeggio (C5–E5–G5–C6) played when a call starts — a
 * cheerful little lift. Also the user gesture that unlocks audio for the session.
 */
export function playStartChime(): void {
  playChime(
    [
      { freq: 523.25, at: 0, dur: 0.18 },
      { freq: 659.25, at: 0.09, dur: 0.18 },
      { freq: 783.99, at: 0.18, dur: 0.2 },
      { freq: 1046.5, at: 0.27, dur: 0.32 },
    ],
    { peak: 0.09, attack: 0.025, cutoff: 3200 },
  );
}

/** Upbeat falling major arpeggio (G5–E5–C5) played when the call ends. */
export function playEndChime(): void {
  playChime(
    [
      { freq: 783.99, at: 0, dur: 0.16 },
      { freq: 659.25, at: 0.09, dur: 0.18 },
      { freq: 523.25, at: 0.18, dur: 0.32 },
    ],
    { peak: 0.09, attack: 0.025, cutoff: 3200 },
  );
}

/** Begin the thinking pad. Safe to call repeatedly — a second call is ignored. */
export function startThinkingSound(): void {
  if (nodes) return; // already playing
  const audio = getContext();
  if (!audio) return;
  // A user gesture (the "Talk" click) already unlocked audio, but the context
  // can still be suspended after focus changes — resume defensively.
  void audio.resume();

  const now = audio.currentTime;

  const master = audio.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(TARGET_GAIN, now + FADE_IN_SECS);
  master.connect(audio.destination);

  // Keep it soft and muffled so the tone never competes with speech.
  const lowpass = audio.createBiquadFilter();
  lowpass.type = "lowpass";
  lowpass.frequency.value = 700;
  lowpass.connect(master);

  // A calm open fifth (G3 + D4), slightly detuned for warmth.
  const oscillators = [196, 294].map((freq, i) => {
    const osc = audio.createOscillator();
    osc.type = "sine";
    osc.frequency.value = freq;
    osc.detune.value = i === 0 ? -4 : 4;
    osc.connect(lowpass);
    osc.start(now);
    return osc;
  });

  // Tremolo: a ~1.2 Hz LFO gently swells the master gain so it pulses.
  const tremolo = audio.createOscillator();
  tremolo.type = "sine";
  tremolo.frequency.value = 1.2;
  const tremoloDepth = audio.createGain();
  tremoloDepth.gain.value = TARGET_GAIN * 0.45; // pulse between ~55% and 100%
  tremolo.connect(tremoloDepth);
  tremoloDepth.connect(master.gain);
  tremolo.start(now);

  nodes = { master, oscillators, tremolo };
}

/** Fade out and tear down the thinking pad. Safe to call when not playing. */
export function stopThinkingSound(): void {
  if (!ctx || !nodes) return;
  const audio = ctx;
  const { master, oscillators, tremolo } = nodes;
  nodes = null;

  const now = audio.currentTime;
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(master.gain.value, now);
  master.gain.linearRampToValueAtTime(0, now + FADE_OUT_SECS);

  const stopAt = now + FADE_OUT_SECS + 0.02;
  [...oscillators, tremolo].forEach((osc) => {
    try {
      osc.stop(stopAt);
    } catch {
      // already stopped — ignore
    }
  });
}
